import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Archive, FileText, FolderOpen, MapPin, GitBranch, UserCog, Settings } from 'lucide-react'
import Spinner from 'components/Spinner'
import { initializeAdminLocations } from 'slices/locationSlice'
import { initializeAdminProjects } from 'slices/projectSlice'
import { initializeAdminPortfolios } from 'slices/portfolioSlice'
import { initializeForScope } from 'slices/documentLibrarySlice'
import { initializeMyLocationImages } from 'slices/locationImageSlice'
import { initializeAdminTemplates } from 'slices/templateSlice'

const Admins = () => {
	const dispatch = useDispatch()
	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin
	const userEmail = user.email

	const {
		ids: locationIds,
		isLoading: isLocationLoading,
		isInitialized: locationsInitialized,
	} = useSelector(state => state.locations)

	const {
		ids: projectIds,
		isLoading: isProjectLoading,
		isInitialized: projectsInitialized,
	} = useSelector(state => state.projects)

	const {
		ids: portfolioIds,
		isLoading: isPortfolioLoading,
		isInitialized: portfoliosInitialized,
	} = useSelector(state => state.portfolios)

	const {
		ids: libraryDocumentIds,
		isLoading: libraryDocumentsLoading,
		initializedScopes: libraryInitializedScopes,
	} = useSelector(state => state.libraryDocuments)
	const myLibraryInitialized = libraryInitializedScopes.includes('my-library')

	const {
		entities: locationImages,
		ids: locationImageIds,
		isLoading: locationImagesLoading,
		isInitialized: locationImagesInitialized,
	} = useSelector(state => state.locationImages)

	const {
		entities: templates,
		ids: templateIds,
		isLoading: templatesLoading,
		isInitialized: templatesInitialized,
	} = useSelector(state => state.templates)

	useEffect(() => {
		dispatch(initializeAdminLocations())
		dispatch(initializeAdminProjects())
		dispatch(initializeAdminPortfolios())
		dispatch(initializeForScope({ scope: 'my-library' }))
		dispatch(initializeMyLocationImages())
		dispatch(initializeAdminTemplates())
	}, [
		dispatch,
		locationsInitialized,
		projectsInitialized,
		portfoliosInitialized,
		myLibraryInitialized,
		locationImagesInitialized,
		templatesInitialized,
	])

	const locationsLength = locationIds.length
	const projectsLength = projectIds.length
	const portfoliosLength = portfolioIds.length
	const libraryDocumentsLength = libraryDocumentIds.length
	const locationImagesLength = locationImageIds.length

	const taskTemplatesCount = templateIds.filter(id => templates[id]?.templateType === 'task').length

	const siteDetailTemplatesCount = templateIds.filter(
		id => templates[id]?.templateType === 'sitedetail',
	).length

	const stats = [
		{
			label1: 'Locations: ',
			value1: locationsLength,
			icon: <MapPin className="text-[#0080fe] w-9 h-9" />,
			link: '/admin/locations',
		},
		{
			label1: 'Projects: ',
			value1: projectsLength,
			icon: <GitBranch className="text-[#0080fe] w-9 h-9" />,
			link: '/admin/projects',
		},
		{
			label1: 'Portfolios: ',
			value1: portfoliosLength,
			icon: <FolderOpen className="text-[#0080fe] w-9 h-9" />,
			link: '/admin/portfolios',
		},
		{
			label1: 'Documents: ',
			value1: libraryDocumentsLength,
			label2: 'Images: ',
			value2: locationImagesLength,
			icon: <Archive className="text-[#0080fe] w-9 h-9" />,
			link: '/admin/library',
		},
		{
			label1: 'Task Templates: ',
			value1: taskTemplatesCount,
			label2: 'Site Detail Templates: ',
			value2: siteDetailTemplatesCount,
			icon: <FileText className="text-[#0080fe] w-9 h-9" />,
			link: '/admin/templates',
		},
		{
			label1: 'Account Settings',
			label2: '',
			value1: null,
			value2: userEmail,
			icon: <UserCog className="text-[#0080fe] w-9 h-9" />,
			link: '/admin/userprofile',
		},
	]

	if (isLocationLoading || isProjectLoading || isPortfolioLoading)
		return <Spinner containerHeight="50vh" />

	return (
		<div className="space-y-8">
			{/* Stats Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
				{stats.map(stat => (
					<Link to={stat.link} key={stat.label1} className="no-underline">
						<div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition transform p-4">
							<div className="flex items-center space-x-4">
								{stat.icon}
								{stat.value2 !== undefined ? (
									<div>
										<p className="text-gray-500 text-sm">
											{stat.label1}
											<span className="mr-1 text-lg text-black">{stat.value1}</span>
										</p>
										<p className="text-gray-500 text-sm">
											{stat.label2}
											<span className="mr-1 text-lg text-black">{stat.value2}</span>
										</p>
									</div>
								) : (
									<div>
										<p className="text-gray-500 text-sm">
											<span className="mr-1 text-lg">{stat.label1}</span>
											<span className="mr-1 text-lg text-black">{stat.value1}</span>
										</p>
									</div>
								)}
							</div>
						</div>
					</Link>
				))}
			</div>
		</div>
	)
}

export default Admins
